import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { handlePlanLimit } from "@/lib/planLimit";
import DashboardLayout from "@/components/DashboardLayout";
import { PaperPlaneTilt, Plus, Trash, Code, ChatCircleDots, Robot, Copy, CheckCircle } from "@phosphor-icons/react";

export default function BotModule() {
  const [config, setConfig] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [widgetUrl, setWidgetUrl] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(`chat-${Math.random().toString(36).slice(2, 8)}`);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/bot/config").then((r) => setConfig(r.data));
    api.get("/bot/snippet").then((r) => { setSnippet(r.data.snippet); setWidgetUrl(r.data.widget_url); });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages]);

  const save = async () => {
    const { data } = await api.put("/bot/config", {
      business_name: config.business_name,
      tone: config.tone,
      welcome_message: config.welcome_message,
      faqs: config.faqs || [],
    });
    setConfig(data);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/bot/chat", { message: userMsg.content, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      if (handlePlanLimit(err)) {
        setMessages((m) => [...m, { role: "assistant", content: "Plan chat limit reached for this month. Upgrade to keep chatting." }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble responding right now." }]);
      }
    } finally { setBusy(false); }
  };

  const updateFaq = (i, key, value) => {
    const next = [...(config.faqs || [])];
    next[i] = { ...next[i], [key]: value };
    setConfig({ ...config, faqs: next });
  };
  const addFaq = () => setConfig({ ...config, faqs: [...(config.faqs || []), { q: "", a: "" }] });
  const removeFaq = (i) => setConfig({ ...config, faqs: config.faqs.filter((_, idx) => idx !== i) });

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!config) return <DashboardLayout title="Customer Response Bot"><div className="p-12 text-center">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout
      title="Customer Response Bot"
      actions={<button onClick={save} className="btn-primary text-sm" data-testid="save-bot-config-btn"><CheckCircle size={16} weight="fill" /> Save changes</button>}
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Config */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <div className="cell-strong bg-white p-6">
            <div className="overline text-[var(--ink-soft)]">/ Identity</div>
            <div className="font-display text-2xl mt-1 mb-4">Train your AI employee</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Business name" col={2}>
                <input className="input-rc" value={config.business_name} onChange={(e) => setConfig({...config, business_name: e.target.value})} data-testid="business-name-input" />
              </Field>
              <Field label="Tone">
                <select className="input-rc" value={config.tone} onChange={(e) => setConfig({...config, tone: e.target.value})} data-testid="tone-select">
                  <option value="friendly">Friendly</option>
                  <option value="professional">Professional</option>
                  <option value="playful">Playful</option>
                  <option value="concise">Concise</option>
                </select>
              </Field>
              <Field label="Welcome message">
                <input className="input-rc" value={config.welcome_message} onChange={(e) => setConfig({...config, welcome_message: e.target.value})} data-testid="welcome-input" />
              </Field>
            </div>
          </div>

          <div className="cell-strong bg-white p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline text-[var(--ink-soft)]">/ Knowledge</div>
                <div className="font-display text-2xl mt-1">FAQs</div>
              </div>
              <button onClick={addFaq} className="btn-secondary text-sm" data-testid="add-faq-btn"><Plus size={14} weight="bold" /> Add FAQ</button>
            </div>
            <div className="space-y-3">
              {(config.faqs || []).map((f, i) => (
                <div key={i} className="cell p-4 grid grid-cols-12 gap-2" data-testid={`faq-row-${i}`}>
                  <input className="input-rc col-span-5" placeholder="Question" value={f.q} onChange={(e) => updateFaq(i, "q", e.target.value)} />
                  <input className="input-rc col-span-6" placeholder="Answer" value={f.a} onChange={(e) => updateFaq(i, "a", e.target.value)} />
                  <button onClick={() => removeFaq(i)} className="col-span-1 border border-[var(--bad)] text-[var(--bad)] hover:bg-[var(--bad)] hover:text-white" data-testid={`remove-faq-${i}`}><Trash size={14} weight="bold" className="mx-auto" /></button>
                </div>
              ))}
              {(!config.faqs || config.faqs.length === 0) && <div className="text-sm text-[var(--ink-soft)]">No FAQs yet — your bot still works, but training it makes replies sharper.</div>}
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--line)] text-sm text-[var(--ink-soft)]">
              Need longer answers (policies, docs, pricing tables)? Add them in the{" "}
              <a href="/dashboard/knowledge" className="font-mono-rc underline text-[var(--ink)]" data-testid="bot-link-to-kb">
                Knowledge Base →
              </a>
            </div>
          </div>

          <div className="cell-strong bg-[var(--ink)] text-white p-6">
            <div className="flex items-center gap-2 mb-3"><Code size={20} weight="duotone" className="text-[var(--accent)]" />
              <div className="overline text-white/60">/ Embed snippet</div>
            </div>
            <div className="font-display text-2xl mb-2">Drop this on your website</div>
            <p className="text-sm text-white/60 mb-3">Paste it just before <code className="bg-white/10 px-1">{`</body>`}</code>. The widget loads instantly.</p>
            <div className="bg-black/40 p-4 font-mono-rc text-xs text-white/90 break-all border border-white/10" data-testid="snippet-code">{snippet}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={copySnippet} className="btn-accent text-sm" data-testid="copy-snippet-btn">
                <Copy size={16} weight="bold" /> {copied ? "Copied!" : "Copy snippet"}
              </button>
              <a href={widgetUrl} target="_blank" rel="noreferrer" className="btn-secondary text-sm" data-testid="widget-demo-link">
                Open demo page →
              </a>
            </div>
          </div>
        </div>

        {/* Live chat preview */}
        <div className="col-span-12 lg:col-span-5">
          <div className="cell-strong bg-white h-full flex flex-col" style={{ minHeight: 600 }}>
            <div className="px-4 py-3 border-b border-[var(--line)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[var(--brand)] flex items-center justify-center"><Robot size={18} weight="duotone" color="#FFD600" /></div>
                <div>
                  <div className="font-medium text-sm">{config.business_name}</div>
                  <div className="text-xs text-[var(--good)] font-mono-rc">● ONLINE</div>
                </div>
              </div>
              <span className="overline text-[var(--ink-soft)]">Live preview</span>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2 bg-[var(--bg-soft)]" data-testid="chat-window">
              <Bubble role="assistant">{config.welcome_message}</Bubble>
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role}>{m.content}</Bubble>
              ))}
              {busy && <Bubble role="assistant">…</Bubble>}
            </div>
            <form onSubmit={send} className="p-3 border-t border-[var(--line)] flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Try: What are your hours?"
                className="input-rc"
                data-testid="chat-input"
              />
              <button type="submit" disabled={busy} className="btn-primary px-4" data-testid="chat-send-btn"><PaperPlaneTilt size={18} weight="fill" /></button>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, col = 1, children }) {
  return (
    <label className={`block col-span-${col === 2 ? "2" : "1"}`}>
      <div className="overline text-[var(--ink-soft)] mb-2">{label}</div>
      {children}
    </label>
  );
}

function Bubble({ role, children }) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="bg-[var(--brand)] text-white px-3 py-2 max-w-[80%] text-sm"
          data-testid="bot-chat-message"
          data-role="user"
        >
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex">
      <div
        className="bg-white border border-[var(--ink)] px-3 py-2 max-w-[80%] text-sm"
        data-testid="bot-chat-message"
        data-role="assistant"
      >
        {children}
      </div>
    </div>
  );
}
